import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Save, Send, Download, Copy, Trash2, CheckCircle2, CalendarDays } from "lucide-react";
import { toast } from "sonner";
import api, { API, getToken } from "@/lib/api";
import LineItemsEditor from "@/components/LineItemsEditor";
import PaymentScheduleEditor from "@/components/PaymentScheduleEditor";

const blank = {
  client_name: "", client_email: "", client_phone: "",
  event_date: "", event_location: "",
  guest_count: 0, menu_description: "",
  line_items: [],
  tax: 0,
  installments: [],
  memo: "",
  notes: "",
};

const inputCls = "w-full bg-[#0a0a0a] border border-white/10 px-3 py-2 text-sm focus:border-[#C9963F] focus:outline-none text-white";
const boxInputCls = inputCls;

export default function InvoiceEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;
  const [form, setForm] = useState(blank);
  const [inv, setInv] = useState(null);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);

  function set(k, v) { setForm((f) => ({ ...f, [k]: v })); }

  useEffect(() => {
    if (!isEdit) { setLoading(false); return; }
    (async () => {
      try {
        const { data } = await api.get(`/invoices/${id}`);
        setInv(data);
        setForm({ ...blank, ...data });
      } catch { toast.error("Could not load invoice"); }
      finally { setLoading(false); }
    })();
  }, [id, isEdit]);

  const totals = useMemo(() => {
    const items = form.line_items || [];
    const gc = Number(form.guest_count) || 0;
    let sub = 0;
    for (const it of items) {
      const up = Number(it.unit_price) || 0;
      if (it.kind === "per_person") sub += gc * up;
      else sub += (Number(it.quantity) || 1) * up;
    }
    const total = sub + (Number(form.tax) || 0);
    const installments = form.installments || [];
    const paid = installments.reduce((a, i) => a + (i.paid ? Number(i.amount) || 0 : 0), 0);
    const outstanding = Math.max(total - paid, 0);
    return { subtotal: sub, total, paid, outstanding };
  }, [form.line_items, form.tax, form.installments, form.guest_count]);

  async function save(sendAfter = false) {
    if (!form.client_name.trim()) return toast.error("Client name required");
    setSaving(true);
    try {
      const payload = {
        ...form,
        tax: Number(form.tax) || 0,
        guest_count: Number(form.guest_count) || 0,
        event_date: form.event_date || null,
        line_items: (form.line_items || []).map((it) => ({
          id: it.id, label: it.label || "", kind: it.kind || "flat",
          quantity: Number(it.quantity) || 1, unit_price: Number(it.unit_price) || 0,
        })),
        installments: (form.installments || []).map((it) => ({
          id: it.id, label: it.label || "", amount: Number(it.amount) || 0,
          due_date: it.due_date || null, paid: !!it.paid,
          paid_at: it.paid_at || null, method: it.method || "", payment_link: it.payment_link || "",
        })),
      };
      let res;
      if (isEdit) {
        res = await api.put(`/invoices/${id}`, payload);
      } else {
        res = await api.post("/invoices", payload);
      }
      setInv(res.data);
      setForm((f) => ({ ...f, ...res.data }));
      toast.success(isEdit ? "Saved" : "Invoice created");
      if (!isEdit) {
        navigate(`/admin/invoices/${res.data.id}`, { replace: true });
      }
      if (sendAfter) {
        await api.post(`/invoices/${res.data.id}/send`);
        toast.success("Marked sent — share the client link");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  }

  async function markInstPaid(instId, paid, method) {
    if (!isEdit) return toast.error("Save first");
    try {
      const { data } = await api.post(`/invoices/${id}/installments/${instId}/payment`, { paid, method });
      setInv(data);
      setForm((f) => ({ ...f, ...data }));
      toast.success(paid ? "Marked paid" : "Marked unpaid");
    } catch { toast.error("Update failed"); }
  }

  function copyLink() {
    const t = inv?.public_token;
    if (!t) return;
    const url = `${window.location.origin}/i/${t}`;
    navigator.clipboard.writeText(url).then(() => toast.success("Client link copied"));
  }

  async function download() {
    try {
      const res = await api.get(`/invoices/${id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url;
      a.download = `${inv?.invoice_number || "invoice"}.pdf`; document.body.appendChild(a); a.click(); a.remove();
    } catch { toast.error("Download failed"); }
  }

  if (loading) return <div className="text-white/40 text-sm">Loading…</div>;

  return (
    <div data-testid="invoice-editor-page">
      <Link to="/admin/invoices" className="inline-flex items-center gap-1 text-xs text-white/50 hover:text-white mb-4" data-testid="back-to-invoices"><ArrowLeft size={12} /> Back to invoices</Link>

      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#C9963F] mb-2">
            {isEdit ? inv?.invoice_number || "Invoice" : "New Invoice"}
          </div>
          <h1 className="font-serif-brand text-4xl sm:text-5xl">{form.client_name || "Untitled"}</h1>
          {isEdit && inv?.status && (
            <div className="mt-2 text-[10px] uppercase tracking-[0.2em] text-white/50">Status: <span className="text-[#C9963F]">{inv.status}</span></div>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {isEdit && (
            <>
              <button onClick={copyLink} data-testid="copy-invoice-link" className="inline-flex items-center gap-2 border border-white/15 text-white/80 px-3 py-2 text-sm hover:border-[#C9963F] hover:text-[#C9963F]"><Copy size={14} /> Copy Link</button>
              <button onClick={download} data-testid="download-invoice-pdf" className="inline-flex items-center gap-2 border border-white/15 text-white/80 px-3 py-2 text-sm hover:border-[#C9963F] hover:text-[#C9963F]"><Download size={14} /> PDF</button>
              {inv?.event_date && (
                <Link to="/admin/calendar" data-testid="view-on-calendar" className="inline-flex items-center gap-2 border border-white/15 text-white/80 px-3 py-2 text-sm hover:border-[#C9963F] hover:text-[#C9963F]"><CalendarDays size={14} /> View on Calendar</Link>
              )}
              {inv?.status !== "Paid" && inv?.status !== "Cancelled" && (
                <button onClick={async () => {
                  try {
                    const { data } = await api.patch(`/invoices/${id}/status`, { status: "Paid" });
                    setInv(data); setForm((f) => ({ ...f, ...data }));
                    toast.success("Marked paid");
                  } catch { toast.error("Failed"); }
                }} data-testid="mark-invoice-paid-btn"
                  className="inline-flex items-center gap-2 border border-[#C9963F] text-[#C9963F] px-3 py-2 text-sm hover:bg-[#C9963F]/10"><CheckCircle2 size={14} /> Mark Paid</button>
              )}
            </>
          )}
          <button onClick={() => save(false)} disabled={saving} data-testid="save-invoice-btn" className="inline-flex items-center gap-2 bg-[#C9963F] text-black font-medium px-4 py-2 text-sm hover:bg-[#A67A2D] disabled:opacity-50">
            <Save size={14} /> {saving ? "Saving…" : "Save"}
          </button>
          {isEdit && (
            <button onClick={() => save(true)} disabled={saving} data-testid="send-invoice-btn" className="inline-flex items-center gap-2 bg-white/10 border border-white/20 text-white px-4 py-2 text-sm hover:bg-white/20">
              <Send size={14} /> Save & Mark Sent
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-8">
        <div className="space-y-8">
          <Section title="Client">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5">
              <Field label="Client Name *">
                <input className={inputCls} value={form.client_name} onChange={(e) => set("client_name", e.target.value)} data-testid="input-inv-client-name" autoFocus />
              </Field>
              <Field label="Email">
                <input className={inputCls} value={form.client_email} onChange={(e) => set("client_email", e.target.value)} data-testid="input-inv-client-email" />
              </Field>
              <Field label="Phone">
                <input className={inputCls} value={form.client_phone} onChange={(e) => set("client_phone", e.target.value)} data-testid="input-inv-client-phone" />
              </Field>
              <Field label="Event / Service Date (optional)">
                <input type="date" className={inputCls} value={form.event_date || ""} onChange={(e) => set("event_date", e.target.value)} data-testid="input-inv-event-date" />
              </Field>
              <Field label="Event Location (optional)">
                <input className={inputCls} value={form.event_location} onChange={(e) => set("event_location", e.target.value)} data-testid="input-inv-event-loc" />
              </Field>
              <Field label="Guest Count">
                <input type="number" min="0" className={inputCls} value={form.guest_count ?? 0} onChange={(e) => set("guest_count", Number(e.target.value))} data-testid="input-inv-guest-count" placeholder="Used for per-person line items" />
              </Field>
            </div>
          </Section>

          <Section title="Menu Items (optional)">
            <p className="text-xs text-white/40 mb-3">List the menu items included in this catering. Shown on the customer invoice and PDF.</p>
            <textarea rows={6} className={inputCls} value={form.menu_description} onChange={(e) => set("menu_description", e.target.value)} data-testid="input-inv-menu" placeholder={"e.g.\n• Chicken shawarma platter\n• Beef kebabs\n• Hummus & pita\n• Baklava"} />
          </Section>

          <Section title="Line Items">
            <LineItemsEditor items={form.line_items || []} guestCount={Number(form.guest_count) || 0} onChange={(items) => set("line_items", items)} />
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5">
              <Field label="Tax ($)">
                <input type="number" step="0.01" min="0" className={boxInputCls} value={form.tax ?? 0} onChange={(e) => set("tax", Number(e.target.value))} data-testid="input-inv-tax" />
              </Field>
            </div>
          </Section>

          <Section title="Payment Schedule & Links">
            <p className="text-xs text-white/40 mb-4">
              Add one or more payments (or leave empty if paid in one lump). Paste your Square payment link per row and the client will see a "Pay with Square" button on their invoice page and PDF.
            </p>
            <div className="mb-4 flex flex-wrap gap-2">
              <button type="button" onClick={() => {
                if ((form.installments || []).length && !window.confirm("Replace current schedule?")) return;
                set("installments", [{ id: `i_${Date.now()}`, label: "Full Payment", amount: totals.total || 0, due_date: null, paid: false, method: "", payment_link: "" }]);
              }} className="text-[10px] uppercase tracking-wider border border-white/15 text-white/70 hover:border-[#C9963F] hover:text-[#C9963F] px-3 py-2" data-testid="inv-preset-full">Single Payment</button>
              <button type="button" onClick={() => {
                if ((form.installments || []).length && !window.confirm("Replace current schedule?")) return;
                const t = totals.total || 0;
                const dep = +(t * 0.5).toFixed(2);
                set("installments", [
                  { id: `i_${Date.now()}_1`, label: "Deposit", amount: dep, due_date: null, paid: false, method: "", payment_link: "" },
                  { id: `i_${Date.now()}_2`, label: "Balance", amount: +(t - dep).toFixed(2), due_date: null, paid: false, method: "", payment_link: "" },
                ]);
              }} className="text-[10px] uppercase tracking-wider border border-white/15 text-white/70 hover:border-[#C9963F] hover:text-[#C9963F] px-3 py-2" data-testid="inv-preset-5050">50 / 50</button>
              {(form.installments || []).length > 0 && (
                <button type="button" onClick={() => window.confirm("Clear all payments?") && set("installments", [])}
                  className="text-[10px] uppercase tracking-wider border border-white/15 text-white/50 hover:border-red-400 hover:text-red-400 px-3 py-2" data-testid="inv-clear">Clear</button>
              )}
            </div>
            <PaymentScheduleEditor
              installments={form.installments || []}
              total={totals.total}
              onChange={(items) => set("installments", items)}
              onMarkPaid={markInstPaid}
              showPaidControls={isEdit}
            />
          </Section>

          <Section title="Memo (shown to client, optional)">
            <textarea rows={3} className={inputCls} value={form.memo} onChange={(e) => set("memo", e.target.value)} data-testid="input-inv-memo" placeholder="Thank you note, service description, etc." />
          </Section>

          <Section title="Internal Notes (not shown to client)">
            <textarea rows={3} className={inputCls} value={form.notes} onChange={(e) => set("notes", e.target.value)} data-testid="input-inv-notes" />
          </Section>
        </div>

        <aside className="lg:sticky lg:top-24 self-start bg-[#0e0e0e] border border-white/10 p-5" data-testid="inv-summary">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#C9963F] mb-3">Summary</div>
          <Row label="Subtotal" value={money(totals.subtotal)} />
          <Row label="Tax" value={money(form.tax)} />
          <div className="border-t border-white/10 mt-2 pt-2">
            <div className="flex items-baseline justify-between">
              <span className="text-[10px] uppercase tracking-wider text-white/50">Total</span>
              <span className="font-serif-brand text-2xl text-[#C9963F]">{money(totals.total)}</span>
            </div>
          </div>
          <div className="mt-4 pt-4 border-t border-white/10 space-y-2">
            <Row label="Paid" value={money(totals.paid)} />
            <div className="flex items-baseline justify-between text-sm">
              <span className="text-white/50">Outstanding</span>
              <span className={totals.outstanding === 0 ? "text-[#C9963F]" : "text-amber-300"} data-testid="inv-outstanding">{money(totals.outstanding)}</span>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}

function money(n) { return `$${Number(n || 0).toFixed(2)}`; }

function Section({ title, children }) {
  return (
    <section>
      <div className="text-[10px] uppercase tracking-[0.3em] text-[#C9963F] border-b border-white/10 pb-2 mb-5">{title}</div>
      {children}
    </section>
  );
}
function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase tracking-[0.2em] text-white/50 mb-2">{label}</div>
      {children}
    </label>
  );
}
function Row({ label, value }) {
  return (
    <div className="flex items-baseline justify-between py-1 text-sm">
      <span className="text-white/50">{label}</span>
      <span className="text-white">{value}</span>
    </div>
  );
}
